import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATO_LABEL = {
  pending: "In attesa di accettazione",
  accettata_mail_inviata: "Accettata · in attesa di conferma genitore",
  confermata_genitore: "Confermata definitivamente dal genitore",
  pagata_da_verificare: "Pagamento da verificare",
  pagata: "Pagata",
  scaduta: "Scaduta",
  rejected: "Rifiutata",
};

// Mappa numero giorno (0=LUN..6=DOM) → nome breve italiano. Usato per visualizzare
// in modo leggibile la stringa "giorni_scelti" memorizzata sul DB (es. "0,2" → "LUN, MER").
const _DAY_NAMES_BY_NUM = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
function formatGiorniScelti(raw) {
  if (raw == null) return "—";
  const tokens = (Array.isArray(raw) ? raw : String(raw).replace(/;/g, ",").split(","))
    .map(t => String(t).trim().toUpperCase())
    .filter(Boolean);
  if (tokens.length === 0) return "—";
  const labels = tokens.map(t => {
    if (/^-?\d+$/.test(t)) {
      const n = parseInt(t, 10);
      if (n >= 0 && n < _DAY_NAMES_BY_NUM.length) return _DAY_NAMES_BY_NUM[n];
      return t;
    }
    const k = t.replace(/\.$/, "");
    if (_DAY_NAMES_BY_NUM.includes(k)) return k;
    // Forme estese (LUNEDI, MARTEDI, ...)
    const ext = { LUNEDI: "LUN", MARTEDI: "MAR", MERCOLEDI: "MER", GIOVEDI: "GIO", VENERDI: "VEN", SABATO: "SAB", DOMENICA: "DOM" };
    return ext[k] || k;
  });
  return labels.join(", ");
}

export default function ConfermaIscrizionePublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmedNow, setConfirmedNow] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/mensa-matrice/conferma-iscrizione/info/${encodeURIComponent(token)}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Errore caricamento. Link non valido o scaduto.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const conferma = async () => {
    if (!window.confirm("Confermi DEFINITIVAMENTE l'iscrizione? Procederai poi al pagamento secondo le istruzioni indicate.")) return;
    setConfirming(true);
    try {
      await axios.post(`${API_BASE}/api/mensa-matrice/conferma-iscrizione/confirm/${encodeURIComponent(token)}`);
      setConfirmedNow(true);
      // Reload
      const r = await axios.get(`${API_BASE}/api/mensa-matrice/conferma-iscrizione/info/${encodeURIComponent(token)}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore conferma");
    } finally {
      setConfirming(false);
    }
  };

  const caricaContabile = async () => {
    if (!uploadFile) { setUploadErr("Seleziona prima il file della contabile"); return; }
    setUploadErr(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const r = await axios.post(
        `${API_BASE}/api/mensa-matrice/conferma-iscrizione/upload-contabile/${encodeURIComponent(token)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadResult(r.data);
      // Reload info to refresh stato
      const info = await axios.get(`${API_BASE}/api/mensa-matrice/conferma-iscrizione/info/${encodeURIComponent(token)}`);
      setData(info.data);
      setUploadFile(null);
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || "Errore caricamento contabile");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Wrapper><div data-testid="cp-loading" style={{ textAlign: "center", padding: 40 }}>Caricamento…</div></Wrapper>;
  if (err && !data) return <Wrapper><Errore msg={err} /></Wrapper>;

  const i = data?.iscrizione || {};
  const p = data?.pagamento || {};
  const already = data?.already_confirmed;
  // 🆕 Quando la contabile è caricata (= pagamento eseguito), mostriamo SOLO
  // il box "PAGAMENTO ESEGUITO CORRETTAMENTE" + Dettagli iscrizione.
  // Nascondiamo: stato corrente, banner conferma vecchio, istruzioni pagamento,
  // sezione upload contabile e bottone "CONFERMO DEFINITIVAMENTE".
  const pagamentoEseguito = !!i.contabile_filename;

  if (pagamentoEseguito) {
    return (
      <Wrapper>
        <h1 style={{ margin: "0 0 8px", color: "#0d47a1", fontSize: 22, fontWeight: 800 }}>
          Conferma iscrizione attività extra
        </h1>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
          Istituto Canossiano · Brescia
        </div>

        <div data-testid="cp-pagamento-ok" style={{ padding: 18, background: "#c8e6c9", border: "2px solid #2e7d32", borderRadius: 6, marginBottom: 18, color: "#1b5e20", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>✅ Ricevuta inviata correttamente</div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.3, marginBottom: 4 }}>PAGAMENTO ESEGUITO CORRETTAMENTE</div>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#2e7d32" }}>La segreteria verificherà la contabile al più presto. Grazie!</div>
        </div>

        {/* Dettagli iscrizione */}
        <Section title="Dettagli iscrizione">
          <Row k="Alunno" v={`${i.alunno || "—"} ${i.alunno_cod ? `(cod. ${i.alunno_cod})` : ""}`} />
          <Row k="Classe / Settore" v={`${i.classe || "—"} · ${i.settore || "—"}`} />
          <Row k="Corso" v={i.corso_descrizione ? `${i.corso_descrizione} (${i.corso_codice})` : i.corso_codice} />
          <Row k="Tariffa" v={i.tariffa_etichetta ? `${i.tariffa_etichetta} — € ${Number(i.tariffa_importo || 0).toFixed(2)}` : `€ ${Number(i.tariffa_importo || 0).toFixed(2)}`} />
          {i.giorni_scelti && i.giorni_scelti.length > 0 && (
            <Row k="Giorni scelti" v={formatGiorniScelti(i.giorni_scelti)} />
          )}
          <Row k="Anno scolastico" v={i.anno} />
        </Section>

        <div style={{ marginTop: 20, fontSize: 10, color: "#888", textAlign: "center" }}>
          Link sicuro generato dalla segreteria. Non condividere con terzi.
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <h1 style={{ margin: "0 0 8px", color: "#0d47a1", fontSize: 22, fontWeight: 800 }}>
        Conferma iscrizione attività extra
      </h1>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
        Istituto Canossiano · Brescia
      </div>

      {/* Stato corrente */}
      <div data-testid="cp-stato" style={{ padding: "8px 12px", marginBottom: 16, borderRadius: 4,
              background: already ? "#e8f5e9" : "#fff3e0",
              border: `1px solid ${already ? "#2e7d32" : "#ef6c00"}`,
              color: already ? "#1b5e20" : "#bf360c",
              fontWeight: 700, fontSize: 13 }}>
        {already ? "✓ " : "⏳ "}{STATO_LABEL[i.stato] || i.stato}
      </div>

      {confirmedNow && (
        <div data-testid="cp-confermato-banner" style={{ padding: 12, background: "#c8e6c9", border: "2px solid #2e7d32", borderRadius: 4, marginBottom: 16, color: "#1b5e20", fontWeight: 700 }}>
          ✅ <b>Conferma registrata con successo.</b><br />
          Adesso procedi con il pagamento seguendo le istruzioni qui sotto.
        </div>
      )}

      {/* Dettagli iscrizione */}
      <Section title="Dettagli iscrizione">
        <Row k="Alunno" v={`${i.alunno || "—"} ${i.alunno_cod ? `(cod. ${i.alunno_cod})` : ""}`} />
        <Row k="Classe / Settore" v={`${i.classe || "—"} · ${i.settore || "—"}`} />
        <Row k="Corso" v={i.corso_descrizione ? `${i.corso_descrizione} (${i.corso_codice})` : i.corso_codice} />
        <Row k="Tariffa" v={i.tariffa_etichetta ? `${i.tariffa_etichetta} — € ${Number(i.tariffa_importo || 0).toFixed(2)}` : `€ ${Number(i.tariffa_importo || 0).toFixed(2)}`} />
        {i.giorni_scelti && i.giorni_scelti.length > 0 && (
          <Row k="Giorni scelti" v={formatGiorniScelti(i.giorni_scelti)} />
        )}
        <Row k="Anno scolastico" v={i.anno} />
      </Section>

      {/* Istruzioni di pagamento */}
      <Section title="Istruzioni di pagamento" highlight>
        {p.istruzioni && (
          <div style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#333", marginBottom: 12, padding: 8, background: "#fafafa", borderRadius: 3 }}>
            {p.istruzioni}
          </div>
        )}
        <Row k="Intestatario" v={p.intestatario || "—"} mono />
        <Row k="IBAN" v={p.iban || "—"} mono testid="cp-iban" />
        {p.banca && <Row k="Banca" v={p.banca} />}
        <Row k="Causale" v={p.causale || "—"} mono testid="cp-causale" />
        {p.contatti && (
          <div style={{ fontSize: 11, color: "#666", marginTop: 12, padding: 8, background: "#f5f5f5", borderRadius: 3 }}>
            <b>Contatti segreteria:</b><br />
            <span style={{ whiteSpace: "pre-wrap" }}>{p.contatti}</span>
          </div>
        )}
      </Section>

      {/* (Sezione "CONFERMO DEFINITIVAMENTE" spostata in fondo, dopo l'upload contabile) */}

      {err && data && <div data-testid="cp-err" style={{ marginTop: 12, padding: 10, background: "#ffebee", color: "#c62828", borderRadius: 3, fontSize: 12 }}>{err}</div>}

      {/* SEZIONE UPLOAD CONTABILE — visibile dopo accettazione segreteria (in_attesa_pagamento, confermata_genitore, pagata_da_verificare) */}
      {data && ["in_attesa_pagamento", "accettata_mail_inviata", "confermata_genitore", "pagata_da_verificare"].includes(i.stato) && (
        <div data-testid="cp-upload-section" style={{ marginTop: 24, padding: 16, background: "#e3f2fd", border: "2px solid #1565c0", borderRadius: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0d47a1", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            📎 Carica la contabile del bonifico
          </div>
          {i.contabile_filename ? (
            <div data-testid="cp-contabile-current" style={{ padding: 10, background: "#fff", border: "1px solid #90caf9", borderRadius: 3, marginBottom: 10, fontSize: 12 }}>
              <div style={{ marginBottom: 4, color: "#1b5e20", fontWeight: 700 }}>
                ✅ Contabile già caricata
              </div>
              <div style={{ color: "#555", fontSize: 11 }}>
                File: <b>{i.contabile_filename}</b><br />
                Caricata il: {i.contabile_uploaded_at ? new Date(i.contabile_uploaded_at).toLocaleString("it-IT") : "—"}
              </div>
              <a data-testid="cp-contabile-link"
                 href={`${API_BASE}/api/mensa-matrice/conferma-iscrizione/download-contabile/${encodeURIComponent(token)}`}
                 target="_blank" rel="noreferrer"
                 style={{ display: "inline-block", marginTop: 6, color: "#1565c0", fontSize: 11, textDecoration: "underline" }}>
                Visualizza la contabile caricata →
              </a>
              <div style={{ marginTop: 8, padding: 8, background: "#fff3e0", border: "1px solid #ef6c00", borderRadius: 3, fontSize: 11, color: "#bf360c" }}>
                ⚠️ Hai sbagliato file? Puoi <b>ricaricare</b> qui sotto: il vecchio file verrà sostituito.
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#0d47a1", marginBottom: 12, lineHeight: 1.5 }}>
              Dopo aver effettuato il bonifico secondo le istruzioni sopra indicate, <b>carica qui</b> la ricevuta (PDF, JPG, PNG, WebP — max 8 MB).
              La segreteria verificherà il pagamento e <b>aggiornerà lo stato dell&apos;iscrizione</b>.
            </div>
          )}

          {/* Riquadro informativo "Stato iscrizione" — posizionato qui (sopra ai bottoni)
              su richiesta utente 2026-02-24, così il genitore lo legge proprio prima di caricare. */}
          {!already && (
            <div data-testid="cp-info-stato" style={{ marginBottom: 12, padding: 12, background: "#e3f2fd", border: "2px solid #1565c0", borderRadius: 4, lineHeight: 1.55 }}>
              <div style={{ fontSize: 12, color: "#0d47a1" }}>
                <b>ℹ️ Come controllare lo stato della tua iscrizione</b><br />
                Collegandovi al <b>link con cui avete fatto l&apos;iscrizione</b> potrete vedere — dopo i bottoni per iscriversi — la <b>lista delle vostre iscrizioni e il loro stato</b>.
              </div>
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#c8e6c9", borderLeft: "4px solid #2e7d32", color: "#1b5e20", fontSize: 11 }}>
                Lo stato <b>&quot;💳 Pagata e verificata&quot;</b> indica che l&apos;iscrizione è <b>definitivamente confermata</b>.
              </div>
            </div>
          )}

          <input
            id="cp-upload-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            data-testid="cp-upload-input"
            onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadErr(null); }}
            style={{ display: "none" }}
          />
          {/* PASSO 1: Scegli file — bottone grande/colorato come "label for" che apre il file picker */}
          {!uploadFile && (
            <label
              htmlFor="cp-upload-input"
              data-testid="cp-upload-picker-label"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "16px 16px", marginBottom: 8,
                background: "#fff8e1", border: "2px dashed #ff9800", borderRadius: 6,
                cursor: "pointer", color: "#bf360c", fontSize: 14, fontWeight: 800, letterSpacing: 0.3,
                transition: "background .15s ease, border-color .15s ease",
              }}
              onMouseOver={e => { e.currentTarget.style.background = "#fff3e0"; e.currentTarget.style.borderColor = "#e65100"; }}
              onMouseOut={e => { e.currentTarget.style.background = "#fff8e1"; e.currentTarget.style.borderColor = "#ff9800"; }}
            >
              <span style={{ fontSize: 22 }}>👉</span>
              <span>1. CLICCA QUI PER CARICARE LA RICEVUTA DI PAGAMENTO</span>
            </label>
          )}
          {uploadFile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 8, background: "#e8f5e9", border: "1px solid #66bb6a", borderRadius: 4 }}>
              <span style={{ fontSize: 20 }}>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#1b5e20", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{uploadFile.name}</div>
                <div style={{ fontSize: 10, color: "#558b2f" }}>{(uploadFile.size / 1024).toFixed(1)} KB · pronto al caricamento</div>
              </div>
              <label htmlFor="cp-upload-input"
                style={{ fontSize: 10, color: "#1565c0", textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}>
                cambia file
              </label>
            </div>
          )}
          {uploadErr && (
            <div data-testid="cp-upload-err" style={{ padding: 8, background: "#ffebee", border: "1px solid #ef9a9a", color: "#c62828", borderRadius: 3, fontSize: 11, marginBottom: 8 }}>
              ⚠ {uploadErr}
            </div>
          )}
          {uploadResult && (
            <div data-testid="cp-upload-ok" style={{ padding: 14, background: "#c8e6c9", border: "2px solid #2e7d32", color: "#1b5e20", borderRadius: 4, fontSize: 14, marginBottom: 10, fontWeight: 800, textAlign: "center" }}>
              ✅ Ricevuta inviata correttamente. La segreteria verificherà al più presto.
            </div>
          )}
          {uploading && (
            <div data-testid="cp-upload-loading"
                 style={{ padding: "14px 16px", background: "#fff3e0", border: "2px solid #ef6c00", borderRadius: 4, marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 22, height: 22, border: "3px solid #ffe0b2",
                borderTopColor: "#ef6c00", borderRadius: "50%",
                animation: "cpSpin 0.9s linear infinite", flexShrink: 0,
              }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#bf360c" }}>
                  Attendere conferma caricamento documento…
                </div>
                <div style={{ fontSize: 11, color: "#6d4c00", marginTop: 2 }}>
                  Non chiudere la pagina. L&apos;operazione può richiedere qualche secondo.
                </div>
              </div>
              <style>{`@keyframes cpSpin { from {transform:rotate(0deg)} to {transform:rotate(360deg)} }`}</style>
            </div>
          )}
          <button
            data-testid="cp-upload-btn"
            disabled={!uploadFile || uploading}
            onClick={caricaContabile}
            style={{
              width: "100%", padding: "14px 16px",
              background: (!uploadFile || uploading) ? "#90caf9" : "#1565c0",
              color: "white", border: "none", fontSize: 14, fontWeight: 800,
              cursor: (!uploadFile || uploading) ? "default" : "pointer",
              borderRadius: 4, letterSpacing: 0.5,
              boxShadow: (!uploadFile || uploading) ? "none" : "0 2px 8px rgba(21,101,192,0.3)",
            }}
          >
            {uploading ? "Caricamento in corso…" : (i.contabile_filename ? "📎 INVIA ALLA SEGRETERIA UNA NUOVA RICEVUTA DI PAGAMENTO" : (uploadFile ? "👉 2. INVIA ALLA SEGRETERIA LA RICEVUTA DI PAGAMENTO" : "📎 INVIA ALLA SEGRETERIA LA RICEVUTA DI PAGAMENTO"))}
          </button>
        </div>
      )}

      {/* Riquadro informativo "Stato iscrizione" è ora dentro alla sezione "Carica contabile"
          (sopra ai bottoni di scelta file) per una sequenza di lettura più naturale. */}


      <div style={{ marginTop: 30, fontSize: 10, color: "#888", textAlign: "center" }}>
        Link sicuro generato dalla segreteria. Non condividere con terzi.<br />
        {data?.expires_at && `Scadenza: ${new Date(data.expires_at).toLocaleString("it-IT")}`}
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)", padding: "30px 16px" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", background: "white", borderRadius: 8, padding: "28px 28px 24px",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        {children}
      </div>
    </div>
  );
}

function Errore({ msg }) {
  return (
    <div data-testid="cp-error" style={{ padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>⛔</div>
      <h2 style={{ color: "#c62828", margin: "0 0 12px" }}>Impossibile caricare la pagina</h2>
      <p style={{ color: "#666", fontSize: 13, margin: "0 0 16px" }}>{msg}</p>
      <p style={{ fontSize: 11, color: "#888" }}>Contatta la segreteria per ricevere un nuovo link.</p>
    </div>
  );
}

function Section({ title, children, highlight }) {
  return (
    <div style={{ marginBottom: 16, padding: 14, background: highlight ? "#e3f2fd" : "#fafafa",
                  border: `1px solid ${highlight ? "#1565c0" : "#e0e0e0"}`, borderRadius: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: highlight ? "#0d47a1" : "#666", letterSpacing: 0.5, marginBottom: 8, textTransform: "uppercase" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v, mono, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", padding: "4px 0", fontSize: 12, borderBottom: "1px dashed #e0e0e0" }}>
      <div style={{ width: 130, color: "#666", fontWeight: 600, flexShrink: 0 }}>{k}</div>
      <div style={{ flex: 1, color: "#333", fontFamily: mono ? "Consolas, monospace" : "inherit", fontWeight: mono ? 700 : 400, wordBreak: "break-word" }}>
        {v}
      </div>
    </div>
  );
}
